import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../config/api';
import Layout from './Layout';
import SmartImage from './SmartImage';
import AddToCartControl from './common/AddToCartControl';
// Removed toast notifications - using inline button feedback instead

interface Product {
  id: number;
  sos_id: number;
  sku: string;
  name: string;
  fullname?: string;
  description?: string;
  sales_price: number;
  available: number;
  category_name?: string;
  vendor_name?: string;
  is_active: boolean;
  taxable: boolean;
  weight?: number;
  weight_unit?: string;
  volume?: number;
  volume_unit?: string;
  custom_fields?: any;
  image_path?: string;
  image_url?: string;
  image_updated_at?: string;
  minimum_order_quantity: number;
  // Additional gateway fields
  feature_highlights?: string;
  important_note?: string;
}

interface CompatibleProduct {
  id: number;
  sku: string;
  name: string;
  sales_price: number;
  image_url?: string;
  available: number;
  category_name?: string;
}

const ProductPage: React.FC = () => {
  // Helper function to get product display name
  const getProductDisplayName = (product: Product) => {
    // Use name field (which contains ShortDescription) as the primary display name
    // Fall back to fullname, then description if name is not available
    return product.name || product.fullname || product.description || '';
  };
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [compatibleProducts, setCompatibleProducts] = useState<CompatibleProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [quantity, setQuantity] = useState(0);
  const [cartQuantity, setCartQuantity] = useState(0);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const isDesktop = windowWidth >= 992;

  // Track window width for responsive layout
  useEffect(() => {
    const handleResize = () => {
      const newWidth = window.innerWidth;
      setWindowWidth(newWidth);
      console.log('📐 ProductPage Window resized:', {
        newWidth,
        hasSidebar: newWidth >= 768,
        availableSpace: newWidth - (newWidth >= 768 ? 260 : 0),
        breakpoint: newWidth < 768 ? 'mobile' : newWidth < 992 ? 'tablet' : newWidth < 1200 ? 'desktop' : 'large'
      });
    };
    window.addEventListener('resize', handleResize);
    
    // Log initial width
    console.log('📐 ProductPage Initial window width:', {
      width: windowWidth,
      hasSidebar: windowWidth >= 768,
      availableSpace: windowWidth - (windowWidth >= 768 ? 260 : 0),
      breakpoint: windowWidth < 768 ? 'mobile' : windowWidth < 992 ? 'tablet' : windowWidth < 1200 ? 'desktop' : 'large'
    });
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load cart quantity for this product
  const loadCartQuantity = async (productId: number) => {
    try {
      const response = await api.get('/cart');
      const items = response.data?.items || response.data?.cart?.items || [];

      if (Array.isArray(items)) {
        const cartItem = items.find((item: any) => {
          const itemProductId = item.product_id || item.product?.id;
          return itemProductId === productId;
        });

        if (cartItem) {
          const cartQty = cartItem.quantity || 0;
          setCartQuantity(cartQty);
          setQuantity(cartQty);
          console.log(`Product ${productId} found in cart with quantity: ${cartQty}`);
          return cartQty;
        }
      }

      console.log(`Product ${productId} not found in cart`);
      setCartQuantity(0);
      setQuantity(0);
      return 0;
    } catch (error) {
      console.error('Failed to load cart quantity:', error);
      return 0;
    }
  };

  // Helper function to get custom field value
  const getCustomFieldValue = (fieldName: string): string | null => {
    if (!product?.custom_fields) {
      console.log('No custom_fields on product');
      return null;
    }

    // If custom_fields is an array
    if (Array.isArray(product.custom_fields)) {
      const field = product.custom_fields.find((f: any) =>
        f.name?.toLowerCase() === fieldName.toLowerCase()
      );
      console.log(`Looking for ${fieldName}, found:`, field);
      return field?.value || null;
    }

    // If custom_fields is an object
    console.log('custom_fields is object, not array');
    return product.custom_fields[fieldName] || product.custom_fields[fieldName.charAt(0).toUpperCase() + fieldName.slice(1)] || null;
  };

  useEffect(() => {
    const fetchProductDetails = async () => {
      try {
        // Fetch product details
        const response = await api.get<Product>(`/products/${productId}`);
        console.log('Product data:', response.data); // Debug log
        console.log('Custom fields:', response.data.custom_fields); // Debug custom fields
        console.log('Is demo product (ID > 10000):', parseInt(productId!) > 10000);
        setProduct(response.data);

        // Load current cart quantity for this product
        await loadCartQuantity(response.data.id);
        
        // Helper function to get custom field value from response data
        const getCustomFieldValueFromData = (data: Product, fieldName: string): string | null => {
          if (!data?.custom_fields) return null;
          
          // If custom_fields is an array
          if (Array.isArray(data.custom_fields)) {
            const field = data.custom_fields.find((f: any) => 
              f.name?.toLowerCase() === fieldName.toLowerCase()
            );
            return field?.value || null;
          }
          
          // If custom_fields is an object
          return data.custom_fields[fieldName] || data.custom_fields[fieldName.charAt(0).toUpperCase() + fieldName.slice(1)] || null;
        };
        
        // Fetch compatible products based on custom fields
        const compatGroup = getCustomFieldValueFromData(response.data, 'compatibility_group') || 
                           getCustomFieldValueFromData(response.data, 'Group');
        if (compatGroup) {
          const compatResponse = await api.get<{ items: CompatibleProduct[] }>('/products', {
            params: {
              custom_field: `compatibility_group:${compatGroup}`,
              exclude_id: productId,
              page_size: 6,
              is_active: true
            }
          });
          setCompatibleProducts(compatResponse.data.items);
        }
        
        setError('');
      } catch (err: any) {
        setError('Failed to load product details');
        console.error('Product fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    if (productId) {
      fetchProductDetails();
    }
  }, [productId]);

  // Listen for cart updates to refresh the quantity
  useEffect(() => {
    const handleCartUpdate = async () => {
      if (product) {
        await loadCartQuantity(product.id);
      }
    };

    window.addEventListener('cartUpdated', handleCartUpdate);
    return () => window.removeEventListener('cartUpdated', handleCartUpdate);
  }, [product]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const handleAddCompatibleProduct = async (compatProduct: CompatibleProduct) => {
    try {
      await api.post('/cart/items', {
        product_id: compatProduct.id,
        quantity: 1
      });

      // Just update cart count - no disruptive toast
      window.dispatchEvent(new Event('cartUpdated'));
    } catch (error: any) {
      console.error('Error adding to cart:', error);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="container mt-5">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading product...</span>
            </div>
            <p className="mt-3">Loading product details...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !product) {
    return (
      <Layout>
        <div className="container mt-5">
          <div className="alert alert-danger">
            <i className="fas fa-exclamation-circle me-2"></i>
            {error || 'Product not found'}
          </div>
          <button 
            className="btn btn-primary"
            onClick={() => navigate('/products')}
          >
            <i className="fas fa-arrow-left me-2"></i>Back to Products
          </button>
        </div>
      </Layout>
    );
  }

  // Log layout info on render
  console.log('📐 ProductPage Rendering with:', {
    windowWidth,
    isDesktop,
    productLoaded: !!product,
    currentBreakpoint: isDesktop ? 'desktop' : 'mobile',
    layoutStrategy: isDesktop ? '66.67%/33.33% flexbox split' : '100% stacked'
  });

  return (
    <Layout>
      <div className="product-page-wrapper">
        <div className="container-fluid" style={{ maxWidth: '1400px' }}>
          <div style={{ 
            display: 'flex',
            flexWrap: isDesktop ? 'nowrap' : 'wrap',
            gap: '30px'
          }}>
            {/* Left Section - Image and Title */}
            <div style={{
              flex: isDesktop ? '0 0 66.66667%' : '0 0 100%',
              maxWidth: isDesktop ? 'calc(66.66667% - 15px)' : '100%'
            }}>
              {/* Product Title */}
              <div className="product-title-container mb-4">
                <h1 className="product-name mb-1">{getProductDisplayName(product)}</h1>
                <div className="text-muted" style={{ fontSize: '15px', marginBottom: '0.35rem' }}>
                  {product.sku}
                </div>
                <h2 className="product-price mb-0">{formatCurrency(product.sales_price)}</h2>
              </div>
              
              {/* Image Container */}
              <div className="product-container product-image-box">
                <div className="image-wrapper">
                  <SmartImage
                    src={product.image_url || `/images/products/${product.id}`}
                    alt={getProductDisplayName(product)}
                    className="product-image"
                    productName={getProductDisplayName(product)}
                    productCategory={product.category_name}
                    productSku={product.sku}
                  />
                </div>
              </div>

              {/* Important Note Section - Below Image */}
              {getCustomFieldValue('ImportantNote') && (
                <div className="mt-4">
                  <div className="alert alert-warning" style={{
                    lineHeight: '1.6',
                    fontSize: '0.95rem'
                  }}>
                    <div style={{ marginBottom: '8px' }}>
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      <strong>Important Notice:</strong>
                    </div>
                    <div style={{
                      paddingLeft: '24px',
                      textAlign: 'left'
                    }}>
                      {getCustomFieldValue('ImportantNote')}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Right Section - Details and Actions */}
            <div style={{
              flex: isDesktop ? '0 0 33.33333%' : '0 0 100%',
              maxWidth: isDesktop ? 'calc(33.33333% - 15px)' : '100%'
            }}>
              <div className="product-container product-details-box">
                {/* Overview Section */}
                <div className="mb-4">
                  <h3 className="section-title">Overview</h3>
                  <p className="section-content">
                    {getCustomFieldValue('LongDescription') || 
                     getCustomFieldValue('long_description') || 
                     product.description || 
                     'Professional-grade equipment designed for optimal performance and reliability in commercial installations.'}
                  </p>
                </div>

                {/* Features Section */}
                <div className="mb-4">
                  <h3 className="section-title">Features</h3>
                  <ul className="features-list">
                    {getCustomFieldValue('FeatureHighlights') ? (
                      getCustomFieldValue('FeatureHighlights')!.split(/[\r\n,;]/).filter(Boolean).slice(0, 5).map((feature: string, index: number) => (
                        <li key={index}>{feature.trim()}</li>
                      ))
                    ) : (
                      <>
                        <li>High-performance components for reliable operation</li>
                        <li>Easy installation and maintenance</li>
                        <li>Industry-standard compatibility</li>
                      </>
                    )}
                  </ul>
                </div>

                {/* Add to Cart Section */}
                <div className="add-to-cart-section">
                  {/* Price Summary */}
                  <div className="price-summary mb-3 p-3" style={{
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    border: '1px solid #e9ecef'
                  }}>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="text-muted">Unit Price:</span>
                      <span className="fw-bold">{formatCurrency(product.sales_price)}</span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="text-muted">Quantity:</span>
                      <span className="fw-bold">{quantity}</span>
                    </div>
                    <hr className="my-2" />
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="h6 mb-0">Total:</span>
                      <span className="h5 mb-0 text-primary">{formatCurrency(product.sales_price * quantity)}</span>
                    </div>
                  </div>

                  {/* Quantity Controls and Add to Cart */}
                  <div className="mb-3">
                    <label className="form-label text-muted mb-2 text-center d-block">Quantity:</label>
                    <AddToCartControl
                      key={`product-${product.id}-cart-${cartQuantity}`}
                      productId={product.id}
                      minimumOrderQuantity={product.minimum_order_quantity}
                      size="md"
                      initialQuantity={cartQuantity}
                      onQuantityChange={(qty) => setQuantity(qty)}
                    />
                    {product.minimum_order_quantity > 1 && (
                      <small className="text-muted d-block mt-2 text-center">Minimum order: {product.minimum_order_quantity} units</small>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Compatible Products */}
          {compatibleProducts.length > 0 && (
            <div className="mt-5">
              <h3 className="h4 mb-4">Compatible Products</h3>
              <div className="row g-4">
                {compatibleProducts.map(compatProduct => (
                  <div key={compatProduct.id} className="col-lg-3 col-md-4 col-sm-6">
                    <div className="compatible-product-card">
                      <div className="compatible-product-image">
                        <SmartImage
                          src={compatProduct.image_url || `/images/products/${compatProduct.id}`}
                          alt={compatProduct.name}
                          className="w-100 h-100 object-fit-contain"
                          productName={compatProduct.name}
                          productCategory={compatProduct.category_name}
                          productSku={compatProduct.sku}
                        />
                      </div>
                      <div className="compatible-product-details">
                        <h6 className="product-name">{compatProduct.name}</h6>
                        <p className="text-muted small mb-2">SKU: {compatProduct.sku}</p>
                        <div className="d-flex justify-content-between align-items-center">
                          <strong className="text-primary">{formatCurrency(compatProduct.sales_price)}</strong>
                          <button 
                            className="btn btn-sm btn-primary"
                            onClick={() => handleAddCompatibleProduct(compatProduct)}
                            disabled={false}
                          >
                            Add
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .product-page-wrapper {
          background-color: #f8f9fa;
          min-height: calc(100vh - 200px);
          padding: 0 0 3rem 0;
        }
        
        .product-title-container {
          background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
          padding: 2rem;
          border-radius: 12px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.08);
          border: 1px solid rgba(0,0,0,0.05);
        }
        
        .product-name {
          color: #2c5aa0;
          font-size: 1.75rem;
          font-weight: 600;
          margin: 0 0 0.25rem 0;
          line-height: 1.2;
        }
        
        .product-price {
          color: #dc3545;
          font-size: 1.5rem;
          font-weight: 600;
          margin: 0;
        }
        
        .product-container {
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.08);
          border: 1px solid rgba(0,0,0,0.05);
          height: 100%;
          overflow: hidden;
        }
        
        .product-image-box {
          padding: 0;
          position: relative;
          overflow: hidden;
          height: 500px;
        }
        
        .image-wrapper {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #fafafa;
        }
        
        .product-image {
          width: auto;
          height: auto;
          max-width: 100%;
          max-height: 100%;
          object-fit: scale-down;
          display: block;
        }
        
        .product-image-placeholder {
          text-align: center;
          color: #6c757d;
          padding: 3rem;
        }
        
        .product-details-box {
          padding: 2.5rem;
        }
        
        .section-title {
          color: #2c5aa0;
          font-size: 1.25rem;
          font-weight: 600;
          margin-bottom: 1rem;
        }
        
        .section-content {
          color: #212529;
          line-height: 1.6;
          margin-bottom: 0;
        }
        
        .features-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        
        .features-list li {
          position: relative;
          padding-left: 1.5rem;
          margin-bottom: 0.5rem;
          color: #212529;
        }
        
        .features-list li:before {
          content: "•";
          position: absolute;
          left: 0;
          color: #2c5aa0;
          font-weight: bold;
        }
        
        .specifications-table {
          width: 100%;
          border-collapse: collapse;
        }
        
        .specifications-table td {
          padding: 0.75rem 0;
          border-bottom: 1px solid #e9ecef;
        }
        
        .specifications-table tr:last-child td {
          border-bottom: none;
        }
        
        .spec-label {
          font-weight: 500;
          color: #495057;
          width: 140px;
        }
        
        .spec-value {
          color: #212529;
        }
        
        .compatibility-warning {
          color: #dc3545;
          font-weight: 500;
        }
        
        .add-to-cart-section {
          margin-top: 2rem;
          padding-top: 2rem;
          border-top: 1px solid #e9ecef;
        }
        
        .quantity-selector {
          display: flex;
          align-items: center;
        }
        
        .quantity-selector .input-group {
          width: auto;
        }
        
        .quantity-selector .btn {
          width: 32px;
          padding: 0.25rem;
        }
        
        .btn-add-to-cart {
          background-color: var(--color-success);
          color: white;
          border: none;
          padding: 0.75rem 2rem;
          font-weight: 600;
          border-radius: 8px;
          transition: background-color 0.2s ease;
          box-shadow: none;
          font-size: 1rem;
        }
        
        .btn-add-to-cart:hover:not(:disabled) {
          background-color: var(--color-success-hover);
        }
        
        .btn-add-to-cart:disabled {
          background-color: #6c757d;
          opacity: 0.65;
        }

        /* Success feedback button */
        .btn-success-feedback {
          background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%) !important;
          color: white;
          border: none;
          padding: 0.75rem 2rem;
          font-weight: 600;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(34, 197, 94, 0.3);
          font-size: 1rem;
          transition: all 0.3s ease;
          animation: successPulse 0.5s ease;
        }

        @keyframes successPulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.05); }
          100% { transform: scale(1); }
        }

        /* Removed feedback button */
        .btn-removed-feedback {
          background: linear-gradient(135deg, #94a3b8 0%, #64748b 100%) !important;
          color: white;
          border: none;
          padding: 0.75rem 2rem;
          font-weight: 600;
          border-radius: 8px;
          font-size: 1rem;
          transition: all 0.3s ease;
        }
        
        .compatible-product-card {
          background: white;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 2px 4px rgba(0,0,0,0.08);
          height: 100%;
          transition: transform 0.2s;
        }
        
        .compatible-product-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0,0,0,0.12);
        }
        
        .compatible-product-image {
          height: 150px;
          background: #f8f9fa;
          padding: 1rem;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .compatible-product-details {
          padding: 1rem;
        }
        
        .compatible-product-details .product-name {
          font-size: 0.9rem;
          font-weight: 500;
          margin-bottom: 0.5rem;
          color: #212529;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        
        .object-fit-contain {
          object-fit: contain;
        }
        
        
        /* Mobile layout adjustments */
        @media (max-width: 991px) {
          .product-title-container {
            text-align: center;
            padding: 1.5rem;
          }
          
          .product-image-box {
            height: 300px;
            margin-bottom: 1.5rem;
          }
          
          .product-details-box {
            padding: 1.5rem;
          }
          
          .product-name {
            font-size: 1.4rem;
          }
          
          .product-price {
            font-size: 1.2rem;
          }
        }
        
        /* Desktop layout adjustments */
        @media (min-width: 992px) {
          .product-image-box {
            height: 450px;
          }
          
          .product-title-container {
            padding: 1.5rem;
          }
        }
      `}</style>
    </Layout>
  );
};

export default ProductPage;